import React, { ReactNode, useState } from 'react';

import { ActionIcon, Box, ColorInput, Flex, Paper, Popover, Text, useMantineTheme } from '@mantine/core';
import { IconColorPicker, IconTrash } from '@tabler/icons-react';
import { GradientSwatch } from 'components/GradientSwatch';
import { SMART_ASSISTANT_GRADIENT_COLORS_COUNT } from 'shared/constants/smart-assistant';
import { buildLinearGradientValues } from 'shared/utils/smart-assistant';

interface GradientColorPickerProps {
  label: string;
  colors: string[];
  onChange: (colors: string[]) => void;
  error?: ReactNode;
}
export function GradientColorPicker({ label, colors, onChange, error }: GradientColorPickerProps) {
  const theme = useMantineTheme();

  const [isOpen, setIsOpen] = useState(false);

  const borderColor = error ? theme.colors.red[5] : isOpen ? theme.colors.gray[4] : theme.colors.gray[3];

  return (
    <Popover width={200} position="bottom-start" shadow="xs" opened={isOpen} onChange={setIsOpen}>
      <Popover.Target>
        <Box>
          <Text fw={500}>{label}</Text>
          <Flex direction="row" align="center" gap="xs">
            <Paper
              w="100%"
              pl={8}
              pr={6}
              withBorder
              onClick={() => setIsOpen(true)}
              sx={{
                display: 'flex',
                height: 36,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderColor: `${borderColor} !important`
              }}>
              <Flex direction="row" gap="xs" align="center">
                <GradientSwatch colors={colors} />
                <Text c={error ? 'red.5' : undefined}>{colors.join(' - ')}</Text>
              </Flex>
              <ActionIcon size="sm">
                <IconColorPicker style={{ width: 16, height: 16, color: '#000' }} />
              </ActionIcon>
            </Paper>
            {colors.length > 0 && (
              <ActionIcon color="red" onClick={() => onChange([])}>
                <IconTrash />
              </ActionIcon>
            )}
          </Flex>
          <Text c="red.5" fz="xs" mt={3}>
            {error}
          </Text>
        </Box>
      </Popover.Target>
      <Popover.Dropdown>
        <Flex direction="column" gap="xs">
          {buildLinearGradientValues([
            ...colors,
            ...Array(SMART_ASSISTANT_GRADIENT_COLORS_COUNT - colors.length).fill('')
          ]).map(({ percentage, color }, index) => (
            <ColorInput
              key={index}
              label={`${Math.round(percentage)}%`}
              value={color}
              format="hexa"
              withinPortal={false}
              onChange={undefined}
              onChangeEnd={(color) => {
                const newColors = [...colors];
                newColors[index] = color;
                onChange(newColors);
              }}
            />
          ))}
        </Flex>
      </Popover.Dropdown>
    </Popover>
  );
}
